import { merge } from 'lodash';
import { ServiceProvider } from '@goodt-common/service-provider';
import { ProjectDataProviderRepository } from './DataProvider';
import { DremioServiceSymbol } from './consts';

export { Query, SDK, Errors } from 'goodt-dremio-sdk';

export const DREMIO_CONFIG_DESCRIPTOR_PROPS_ACCESSOR = 'dremio';

/**
 * @param {string|number} dataProviderId
 * @return {?{ host:string, connection:string }}
 */
const getDremioSDKConfig = (dataProviderId) => {
    const dataProvider =
        ProjectDataProviderRepository.getById(dataProviderId) ?? ProjectDataProviderRepository.getDefault();

    if (dataProvider != null) {
        const { connector, connection } = dataProvider;
        return { host: connector.url, connection: connection?.code };
    }
    return null;
};

/**
 * Dremio sdk factory
 *
 * @param {import('goodt-dremio-sdk').SDKConfig} dremioRawConfig
 * @param {{ dataProviderId?: string }} [extraConfig={}]
 * @return {SDK} sdk instance
 */
export const SDKFactory = (dremioRawConfig = {}, extraConfig = {}) => {
    const { dataProviderId } = extraConfig;

    const config = getDremioSDKConfig(dataProviderId);

    if (config != null) {
        // eslint-disable-next-line no-param-reassign
        dremioRawConfig.host = config.host;
        // eslint-disable-next-line no-param-reassign
        dremioRawConfig.connection = config.connection;
    }

    return ServiceProvider.use(DremioServiceSymbol).withOptions(dremioRawConfig).get();
};

/**
 * @param {import('./mixin').IWidgetDremioDataSource} datasource
 * @return {SDK}
 */
export const useSDKFactory = (datasource) => {
    const dataProviderId = datasource?.dataProviderId;

    return SDKFactory(undefined, {
        dataProviderId
    });
};

/**
 * @effect {sdk}
 * @param {SDK} sdk
 * @param {import('./mixin').IWidgetDremioDataSource} datasource
 * @return {SDK}
 */
export const useSDKDataProvider = (sdk, datasource) => {
    if (datasource == null) {
        return sdk;
    }
    const config = getDremioSDKConfig(datasource.dataProviderId);
    if (config != null) {
        // eslint-disable-next-line
        sdk.config.host = config.host;
        // eslint-disable-next-line
        sdk.config.connection = config.connection;
    }
    return sdk;
};

/**
 *
 * @param {function(): ElemDescriptor} descriptor
 * @return {function(): any}
 */
export const useDremioDescriptor = (descriptor) => () =>
    merge(
        {
            props: {
                [DREMIO_CONFIG_DESCRIPTOR_PROPS_ACCESSOR]: {
                    type: Object,
                    /** @type {IWidgetDremioDataSource?} */
                    default: null
                }
            }
        },
        descriptor()
    );
